import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Settings2, Loader2, Car, Bike, Gauge, Coins } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { api } from "../lib/api";

const GROUPS = [
  [Coins, "التكلفة", [["liter_price", "سعر اللتر المرجعي (ر.س)", 0.01], ["suspicious_liters", "التعبئة المشبوهة فوق (لتر)", 1]]],
  [Gauge, "الانحراف", [["deviation_pct", "نسبة الانحراف المسموحة عن المعيار (%)", 1], ["spike_pct", "تنبيه قفزة وقود المركبة (%)", 1]]],
  [Car, "السيارة", [["car_km_per_liter", "كم تقطع باللتر (كم/لتر)", 0.1], ["car_km_per_order", "متوسط المسافة لكل طلب (كم)", 0.1]]],
  [Bike, "الدباب", [["moto_km_per_liter", "دباب صيني (كم/لتر)", 0.1], ["boxer_km_per_liter", "دباب بوكسر (كم/لتر)", 0.1], ["moto_km_per_order", "متوسط المسافة لكل طلب (كم)", 0.1]]],
];

export const FuelSettingsPanel = () => {
  const [v, setV] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.fuelSettings().then(setV).catch(() => setV({})); }, []);
  const opl = (kpl, kpo) => (Number(kpo) ? (Number(kpl) / Number(kpo)).toFixed(2) : "—");
  const save = async () => {
    setBusy(true);
    try {
      const keys = GROUPS.flatMap((g) => g[2].map((f) => f[0]));
      await api.saveFuelSettings(Object.fromEntries(keys.map((k) => [k, Number(v[k])])));
      toast.success("تم حفظ إعدادات البنزين — تُطبّق على تقارير الاستهلاك والانحراف");
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); } finally { setBusy(false); }
  };
  if (!v) return <p className="text-sm text-slate-400">جارٍ التحميل…</p>;
  return (
    <div className="space-y-4" data-testid="fuel-settings-panel">
      <div className="flex items-center gap-2"><Settings2 className="h-5 w-5 text-emerald-600" /><h3 className="font-heading text-lg font-bold">إعدادات البنزين</h3></div>
      <div className="grid gap-3 md:grid-cols-2">
        {GROUPS.map(([Icon, title, fields]) => (
          <div key={title} className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="mb-3 flex items-center gap-1.5 font-bold text-slate-800"><Icon className="h-4 w-4 text-emerald-600" /> {title}</p>
            <div className="space-y-2.5">{fields.map(([k, label, step]) => (
              <label key={k} className="flex items-center gap-3 text-sm text-slate-600">
                <span className="flex-1">{label}</span>
                <Input type="number" step={step} min={0} value={v[k] ?? ""} onChange={(e) => setV((p) => ({ ...p, [k]: e.target.value }))} className="h-9 w-28 text-center font-mono-nums" data-testid={`fuel-set-${k}`} />
              </label>))}</div>
            {title === "السيارة" && <p className="mt-2 text-[11px] text-slate-500">المعيار: {opl(v.car_km_per_liter, v.car_km_per_order)} طلب لكل لتر</p>}
            {title === "الدباب" && <p className="mt-2 text-[11px] text-slate-500">المعيار: صيني {opl(v.moto_km_per_liter, v.moto_km_per_order)} · بوكسر {opl(v.boxer_km_per_liter, v.moto_km_per_order)} طلب لكل لتر</p>}
          </div>))}
      </div>
      <p className="text-xs text-slate-500">اللترات المتوقعة = الطلبات ÷ (كم/لتر ÷ كم/طلب). إذا زاد استهلاك المندوب عن المتوقع بأكثر من نسبة الانحراف يظهر «هدر»، وإذا قلّ بنفس النسبة يظهر «لافت».</p>
      <Button onClick={save} disabled={busy} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid="fuel-settings-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} حفظ الإعدادات</Button>
    </div>
  );
};
